import React, { useEffect, useState, useCallback } from 'react';
import {
  Modal,
  View,
  Text,
  TouchableOpacity,
  Pressable,
  StyleSheet,
  Platform,
  ScrollView,
} from 'react-native';
import MaterialIcons from '@expo/vector-icons/MaterialIcons';
import { useTheme } from '../context/ThemeContext';
import {
  getConsoleEntries,
  clearConsoleEntries,
  subscribeConsoleCapture,
} from '../utils/consoleCapture';

function levelColor(level, theme) {
  switch (level) {
    case 'error':
      return '#ef5350';
    case 'warn':
      return '#ffb74d';
    case 'info':
      return '#64b5f6';
    case 'debug':
      return theme.isDarkMode ? '#b0bec5' : '#546e7a';
    default:
      return theme.colors.text;
  }
}

function formatTime(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString(undefined, { hour12: false }) + `.${String(d.getMilliseconds()).padStart(3, '0')}`;
  } catch {
    return iso;
  }
}

function formatLogEntriesText(entries) {
  if (!entries.length) return '';
  return entries
    .map((entry) => {
      const level = entry.level ? entry.level.toUpperCase() : 'LOG';
      return `${formatTime(entry.ts)} [${level}] ${entry.text}`;
    })
    .join('\n');
}

async function copyTextToClipboard(text) {
  if (!text) return false;
  if (Platform.OS === 'web' && typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return true;
  }
  if (Platform.OS === 'web' && typeof document !== 'undefined') {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(textarea);
    return ok;
  }
  return false;
}

export default function DebugConsoleModal({ visible, onClose }) {
  const theme = useTheme();
  const styles = getStyles(theme);
  const [logEntries, setLogEntries] = useState([]);
  const [copied, setCopied] = useState(false);

  const refresh = useCallback(() => {
    setLogEntries(getConsoleEntries());
  }, []);

  useEffect(() => {
    if (!visible) return undefined;
    refresh();
    return subscribeConsoleCapture(refresh);
  }, [visible, refresh]);

  useEffect(() => {
    if (!copied) return undefined;
    const timer = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(timer);
  }, [copied]);

  const handleClear = () => {
    clearConsoleEntries();
    refresh();
  };

  const handleCopy = async () => {
    const text = formatLogEntriesText(logEntries);
    if (!text) return;
    try {
      const ok = await copyTextToClipboard(text);
      if (ok) setCopied(true);
    } catch {
      /* ignore clipboard errors */
    }
  };

  const sheet = (
    <Pressable
      style={styles.sheet}
      onPress={(e) => e.stopPropagation?.()}
      {...(Platform.OS === 'web' ? { dataSet: { debugConsoleModalSheet: 'true' } } : {})}
    >
      <View style={styles.headerRow}>
        <Text style={styles.title}>Debug console</Text>
        <View style={styles.headerActions}>
          <TouchableOpacity
            style={styles.iconBtn}
            onPress={handleCopy}
            disabled={logEntries.length === 0}
            accessibilityRole="button"
            accessibilityLabel={copied ? 'Console log copied' : 'Copy console log'}
          >
            <MaterialIcons
              name={copied ? 'check' : 'content-copy'}
              size={22}
              color={theme.colors.text}
              style={logEntries.length === 0 ? { opacity: 0.35 } : undefined}
            />
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.iconBtn}
            onPress={refresh}
            accessibilityRole="button"
            accessibilityLabel="Refresh console log"
          >
            <MaterialIcons name="refresh" size={22} color={theme.colors.text} />
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.iconBtn}
            onPress={handleClear}
            accessibilityRole="button"
            accessibilityLabel="Clear console log"
          >
            <MaterialIcons name="delete-outline" size={22} color={theme.colors.text} />
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.iconBtn}
            onPress={onClose}
            accessibilityRole="button"
            accessibilityLabel="Close debug console"
          >
            <MaterialIcons name="close" size={24} color={theme.colors.text} />
          </TouchableOpacity>
        </View>
      </View>
      <Text style={styles.subtitle}>
        Logs captured since app launch ({logEntries.length} entr{logEntries.length === 1 ? 'y' : 'ies'})
      </Text>
      <ScrollView
        style={styles.logScroll}
        contentContainerStyle={styles.logScrollContent}
        nestedScrollEnabled
        keyboardShouldPersistTaps="handled"
        {...(Platform.OS === 'web' ? { dataSet: { debugConsoleScroll: 'true' } } : {})}
      >
        {logEntries.length === 0 ? (
          <Text style={styles.emptyText}>No console output captured yet.</Text>
        ) : (
          logEntries.map((entry) => (
            <View key={entry.id} style={styles.logLine}>
              <Text style={styles.logMeta}>
                <Text style={styles.logTime}>{formatTime(entry.ts)} </Text>
                <Text style={[styles.logLevel, { color: levelColor(entry.level, theme) }]}>
                  [{entry.level.toUpperCase()}]
                </Text>
              </Text>
              <Text style={[styles.logText, { color: theme.colors.text }]} selectable>
                {entry.text}
              </Text>
            </View>
          ))
        )}
      </ScrollView>
      <TouchableOpacity style={styles.closeBtn} onPress={onClose} activeOpacity={0.85}>
        <Text style={styles.closeBtnText}>Close</Text>
      </TouchableOpacity>
    </Pressable>
  );

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <View
        style={styles.modalRoot}
        {...(Platform.OS === 'web' ? { dataSet: { debugConsoleModalRoot: 'true' } } : {})}
      >
        <Pressable
          style={styles.backdropDismiss}
          onPress={onClose}
          accessibilityRole="button"
          accessibilityLabel="Close debug console"
        />
        <View style={styles.backdropCenter} pointerEvents="box-none">
          {sheet}
        </View>
      </View>
    </Modal>
  );
}

function getStyles(theme) {
  const gold = theme.colors.primary || '#d4af37';
  return StyleSheet.create({
    modalRoot: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: 'transparent',
    },
    backdropDismiss: {
      ...StyleSheet.absoluteFillObject,
      backgroundColor: 'rgba(0, 0, 0, 0.55)',
    },
    backdropCenter: {
      ...StyleSheet.absoluteFillObject,
      justifyContent: 'center',
      alignItems: 'center',
      padding: 16,
      backgroundColor: 'transparent',
    },
    sheet: {
      width: '100%',
      maxWidth: 720,
      maxHeight: '85%',
      backgroundColor: theme.isDarkMode ? '#1a1a1a' : '#FFFFFF',
      borderRadius: 12,
      padding: 16,
      borderWidth: 2,
      borderColor: gold,
      ...(Platform.OS === 'web'
        ? { boxShadow: '0 8px 32px rgba(0,0,0,0.4)' }
        : {}),
    },
    headerRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: 4,
      backgroundColor: 'transparent',
    },
    title: {
      fontSize: 18,
      fontWeight: '700',
      color: theme.colors.heading || theme.colors.text,
      flex: 1,
      backgroundColor: 'transparent',
    },
    headerActions: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: 'transparent',
    },
    iconBtn: {
      padding: 6,
      marginLeft: 4,
      backgroundColor: 'transparent',
    },
    subtitle: {
      fontSize: 12,
      color: theme.colors.text,
      opacity: 0.75,
      marginBottom: 10,
      backgroundColor: 'transparent',
    },
    logScroll: {
      flexGrow: 0,
      flexShrink: 1,
      maxHeight: Platform.OS === 'web' ? '60vh' : 420,
      borderWidth: 1,
      borderColor: theme.isDarkMode ? theme.colors.border : '#E0E0E0',
      borderRadius: 8,
      backgroundColor: theme.isDarkMode ? '#0d0d0d' : '#f8f8f8',
    },
    logScrollContent: {
      padding: 10,
    },
    emptyText: {
      fontSize: 13,
      color: theme.colors.text,
      opacity: 0.7,
      fontStyle: 'italic',
      backgroundColor: 'transparent',
    },
    logLine: {
      marginBottom: 10,
      backgroundColor: 'transparent',
    },
    logMeta: {
      fontSize: 11,
      marginBottom: 2,
      backgroundColor: 'transparent',
    },
    logTime: {
      fontFamily: Platform.OS === 'ios' ? 'Menlo' : Platform.OS === 'android' ? 'monospace' : 'monospace',
      color: theme.isDarkMode ? '#9e9e9e' : '#666666',
      backgroundColor: 'transparent',
    },
    logLevel: {
      fontWeight: '700',
      fontFamily: Platform.OS === 'ios' ? 'Menlo' : Platform.OS === 'android' ? 'monospace' : 'monospace',
      backgroundColor: 'transparent',
    },
    logText: {
      fontSize: 12,
      lineHeight: 18,
      fontFamily: Platform.OS === 'ios' ? 'Menlo' : Platform.OS === 'android' ? 'monospace' : 'monospace',
      backgroundColor: 'transparent',
    },
    closeBtn: {
      marginTop: 12,
      alignSelf: 'center',
      paddingVertical: 10,
      paddingHorizontal: 28,
      borderRadius: 8,
      backgroundColor: gold,
    },
    closeBtnText: {
      fontSize: 15,
      fontWeight: '700',
      color: theme.isDarkMode ? '#1a1a1a' : '#FFFFFF',
      backgroundColor: 'transparent',
    },
  });
}
